import React from 'react';
import { ThumbsUp, MapPin, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { mockCampsites } from '../../../../data/mockCampsites';
import { TopRight } from '@/assets/icon';

const CampsiteSection: React.FC = () => {
  const navigate = useNavigate();

  const getOccupancyColor = (percentage: number) => {
    if (percentage >= 90) return '#F53743';
    if (percentage >= 50) return '#FFB230';
    return '#719C40';
  };

  const getOccupancyLabel = (percentage: number) => {
    if (percentage >= 90) return 'Getting Crowded';
    if (percentage >= 70) return 'Comfortably Busy';
    if (percentage >= 50) return 'Plenty of Space';
    return 'Plenty of Space';
  };

  return (
    <div className="px-4 sm:px-8 lg:px-16 xl:px-24 py-12">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <p
            className="text-gray-500 mb-2"
            style={{
              fontFamily: 'Inter',
              fontSize: '16px',
              fontWeight: 400
            }}
          >
            Where to camp next?
          </p>
          <h2
            className="text-black"
            style={{
              fontFamily: 'Poppins',
              fontSize: '40px',
              fontWeight: 700,
              lineHeight: '48px'
            }}
          >
            Find Your Perfect Campsite.
          </h2>
        </div>
        <button
          onClick={() => navigate('/explore')}
          className="flex items-center gap-2 px-6 py-3 border-2 border-black rounded-full hover:bg-black hover:text-white transition-all"
          style={{
            fontFamily: 'Poppins',
            fontSize: '16px',
            fontWeight: 500
          }}
        >
          View more
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>

      {/* Campsite Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {mockCampsites.slice(0, 3).map((campsite) => (
          <div
            key={campsite.id}
            className="bg-white rounded-2xl overflow-hidden border border-gray-200 hover:shadow-lg transition-all cursor-pointer"
            onClick={() => navigate(`/campsite/${campsite.id}`)}
          >
            {/* Image */}
            <div className="relative h-64 bg-gray-100">
              {campsite.images.length > 0 && (
                <img
                  src={campsite.images[0]}
                  alt={campsite.name}
                  className="w-full h-full object-cover"
                />
              )}
              {/* Rating Badge */}
              <div className="absolute bottom-4 left-4 flex items-center gap-1 bg-white rounded-full px-3 py-1">
                <ThumbsUp className="w-4 h-4 text-orange-500 fill-orange-500" />
                <span
                  style={{
                    fontFamily: 'Poppins',
                    fontSize: '14px',
                    fontWeight: 600
                  }}
                >
                  {campsite.rating}%
                </span>
              </div>
              {/* Location Icon */}
              <div
                className="absolute bottom-4 right-4 rounded-full p-2"
                style={{
                  backgroundColor: '#FFFFFF80',
                  backdropFilter: 'blur(4px)'
                }}
              >
                <img src={TopRight} className='w-4' />
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <h3
                className="text-black mb-2"
                style={{
                  fontFamily: 'Poppins',
                  fontSize: '20px',
                  fontWeight: 600,
                  lineHeight: '28px'
                }}
              >
                {campsite.name}
              </h3>

              <div className="flex items-center gap-1 text-gray-600 mb-3">
                <MapPin className="w-4 h-4" />
                <span
                  style={{
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    fontWeight: 400
                  }}
                >
                  {campsite.location}
                </span>
              </div>

              <p
                className="text-gray-600 mb-4 line-clamp-3"
                style={{
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '20px'
                }}
              >
                {campsite.description}
              </p>

              {/* Occupancy */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <span
                  className="text-gray-700"
                  style={{
                    fontFamily: 'Poppins',
                    fontSize: '14px',
                    fontWeight: 500
                  }}
                >
                  Occupancy: {campsite.occupancy.current}/{campsite.occupancy.total}
                </span>
                <span
                  className="px-3 py-1 rounded-full text-white text-xs font-medium"
                  style={{
                    backgroundColor: getOccupancyColor(campsite.occupancy.percentage),
                    fontFamily: 'Poppins',
                    fontSize: '12px'
                  }}
                >
                  {getOccupancyLabel(campsite.occupancy.percentage)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CampsiteSection;
